import type {
    BindingUpsertRequest,
    CreateFeatureServiceRequest,
    CreateFeatureServiceResult,
    DatasetSummary,
    ForvaltningsDatasetSchema,
    HealthStatus,
    ManualSyncRequest,
    ManualSyncResponse,
    PreflightReport,
    PreflightRequest,
    ScheduleUpsertRequest,
    SyncBinding,
    SyncDirection,
    SyncRequestStats,
    SyncSchedule,
    TranslatedFeatureResult,
} from './types';
import { DIRECTION_PATH } from './types';

/** Feil fra API-et med HTTP-status og evt. server-melding. */
export class ApiError extends Error {
    constructor(
        public readonly status: number,
        message: string,
        public readonly body?: unknown,
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

export interface ApiClientConfig {
    /** Base-URL til forvaltning-api, f.eks. http://localhost:5022 */
    baseUrl: string;
    /** Returnerer gjeldende bearer-token (AGOL-tokenet fra innloggingen). */
    getToken: () => string | undefined;
}

function parseMetrics(text: string): SyncRequestStats {
    const read = (key: string): number => {
        const match = text.match(new RegExp(`^${key}\\s+(\\d+)`, 'm'));
        return match ? Number(match[1]) : 0;
    };
    return {
        pending: read('sync_requests_pending'),
        processing: read('sync_requests_processing'),
        deadLettered: read('sync_requests_dead_lettered'),
    };
}

/**
 * Lettvekts HTTP-klient mot forvaltning-api. Setter `Authorization: Bearer`
 * med AGOL-tokenet og kaster {@link ApiError} ved ikke-2xx-svar.
 */
export function createApiClient(config: ApiClientConfig) {
    const base = config.baseUrl.replace(/\/+$/, '');

    async function request<T>(
        method: string,
        path: string,
        options: { body?: unknown; auth?: boolean; raw?: boolean } = {},
    ): Promise<T> {
        const { body, auth = true, raw = false } = options;
        const headers: Record<string, string> = { Accept: 'application/json' };

        if (auth) {
            const token = config.getToken();
            if (token) headers.Authorization = `Bearer ${token}`;
        }
        if (body !== undefined) headers['Content-Type'] = 'application/json';

        const response = await fetch(`${base}${path}`, {
            method,
            headers,
            body: body !== undefined ? JSON.stringify(body) : undefined,
        });

        if (!response.ok) {
            let parsed: unknown;
            let message = `${response.status} ${response.statusText}`;
            try {
                const text = await response.text();
                if (text) {
                    try {
                        parsed = JSON.parse(text);
                        const err = (parsed as { error?: string })?.error;
                        if (err) message = err;
                    } catch {
                        message = text;
                    }
                }
            } catch {
                /* ignore body-parsing-feil */
            }
            throw new ApiError(response.status, message, parsed);
        }

        if (raw) return (await response.text()) as unknown as T;
        if (response.status === 204) return undefined as unknown as T;

        const text = await response.text();
        return (text ? JSON.parse(text) : undefined) as T;
    }

    return {
        // --- Tjeneste-info / helse ---
        async getServiceInfo(): Promise<{ service: string; status: string }> {
            return request('GET', '/', { auth: false });
        },

        async getHealth(): Promise<HealthStatus> {
            const check = async (path: string): Promise<boolean> => {
                try {
                    const res = await fetch(`${base}${path}`);
                    return res.ok;
                } catch {
                    return false;
                }
            };
            const [healthz, readyz] = await Promise.all([check('/healthz'), check('/readyz')]);
            return { healthz, readyz };
        },

        async getMetrics(): Promise<SyncRequestStats> {
            const text = await request<string>('GET', '/metrics', { auth: false, raw: true });
            return parseMetrics(text);
        },

        // --- Koblinger ---
        async listBindings(onlyEnabled = false): Promise<SyncBinding[]> {
            const q = onlyEnabled ? '?onlyEnabled=true' : '';
            return request('GET', `/admin/bindings/${q}`);
        },

        async getBinding(bindingId: string): Promise<SyncBinding> {
            return request('GET', `/admin/bindings/${encodeURIComponent(bindingId)}`);
        },

        async createBinding(body: BindingUpsertRequest): Promise<SyncBinding> {
            return request('POST', '/admin/bindings/', { body });
        },

        async updateBinding(bindingId: string, body: BindingUpsertRequest): Promise<SyncBinding> {
            return request('PUT', `/admin/bindings/${encodeURIComponent(bindingId)}`, { body });
        },

        async deleteBinding(bindingId: string): Promise<void> {
            return request('DELETE', `/admin/bindings/${encodeURIComponent(bindingId)}`);
        },

        /** Inspiser en eksisterende AGOL-tjeneste og få et mapping-forslag mot NGIS. */
        async preflight(body: PreflightRequest): Promise<PreflightReport> {
            return request('POST', '/admin/bindings/preflight', { body });
        },

        // --- Synkronisering ---
        async requestSync(body: ManualSyncRequest): Promise<ManualSyncResponse> {
            return request('POST', '/sync/request', { body });
        },

        /** Bestill full initial last AGOL → Sentral (alle objekter i de mappede lagene). */
        async startInitialLoad(bindingId: string): Promise<ManualSyncResponse> {
            return request('POST', `/admin/bindings/${encodeURIComponent(bindingId)}/initial-load`);
        },

        // --- Tidsstyrt synk (tidsplaner) ---
        async listSchedules(bindingId: string): Promise<SyncSchedule[]> {
            return request('GET', `/admin/bindings/${encodeURIComponent(bindingId)}/schedules/`);
        },

        async upsertSchedule(
            bindingId: string,
            direction: SyncDirection,
            body: ScheduleUpsertRequest,
        ): Promise<SyncSchedule> {
            return request(
                'PUT',
                `/admin/bindings/${encodeURIComponent(bindingId)}/schedules/${DIRECTION_PATH[direction]}`,
                { body },
            );
        },

        async deleteSchedule(bindingId: string, direction: SyncDirection): Promise<void> {
            return request(
                'DELETE',
                `/admin/bindings/${encodeURIComponent(bindingId)}/schedules/${DIRECTION_PATH[direction]}`,
            );
        },

        // --- Datasett ---
        async listDatasets(system: string): Promise<DatasetSummary[]> {
            return request('GET', `/datasets/?system=${encodeURIComponent(system)}`);
        },

        async getDatasetSchema(system: string, datasetId: string): Promise<ForvaltningsDatasetSchema> {
            return request('GET', `/datasets/${encodeURIComponent(datasetId)}/schema?system=${encodeURIComponent(system)}`);
        },

        async getDatasetFeatures(system: string, datasetId: string, cursor?: string): Promise<TranslatedFeatureResult> {
            const cursorPart = cursor ? `&cursor=${encodeURIComponent(cursor)}` : '';
            return request(
                'GET',
                `/datasets/${encodeURIComponent(datasetId)}/features/esri?system=${encodeURIComponent(system)}${cursorPart}`,
            );
        },

        // --- Provisjonering ---
        async createFeatureService(body: CreateFeatureServiceRequest): Promise<CreateFeatureServiceResult> {
            return request('POST', '/agol/feature-services', { body });
        },
    };
}

export type ApiClient = ReturnType<typeof createApiClient>;
